import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import {
  CREATE_PAYROLL_RUN_DRAFT,
  DELETE_PAYROLL_RUN,
  FINALIZE_PAYROLL_RUN,
  PAYROLL_PREVIEW,
  PAYROLL_RUN,
  PAYROLL_RUNS,
  VOID_PAYROLL_RUN,
} from "src/constants/queries";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { ReportDateRangePicker } from "src/features/Reports/components/ReportDateRangePicker";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";
import { useReportQuery } from "src/features/Reports/hooks/useReportQuery";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import type {
  PayrollPreviewResult,
  PayrollPreviewRow,
  PayrollRunDetail,
  PayrollRunSummary,
  PayrollEmployeeSnapshot,
  PayrollRunStatusEnum,
} from "src/features/Payroll/types/payroll.types";
import type { ReportQueryInput } from "src/features/Reports/types/report.types";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { BtnLoader } from "src/Loader/index";

const headerCellClass =
  "py-3 align-middle text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0] bg-[#f1f5f9] whitespace-nowrap px-2";

function formatRate(row: PayrollPreviewRow): string {
  if (row.payType === "HOURLY" && row.hourlyRateCents != null) {
    return formatCentsToDollarsWithDollarSign(row.hourlyRateCents) + "/hr";
  }
  if (row.payType === "SALARY" && row.salaryCents != null) {
    return formatCentsToDollarsWithDollarSign(row.salaryCents) + (row.salaryPeriod ? `/${row.salaryPeriod}` : "");
  }
  return "—";
}

function formatInstantRange(fromIso: string, toIso: string, storeTz?: string | null): string {
  try {
    const opts: Intl.DateTimeFormatOptions = {
      dateStyle: "medium",
      timeStyle: "short",
    };
    const from = new Date(fromIso);
    const to = new Date(toIso);
    const tz = storeTz || undefined;
    return `${from.toLocaleString(undefined, { ...opts, timeZone: tz })} → ${to.toLocaleString(undefined, { ...opts, timeZone: tz })}`;
  } catch {
    return `${fromIso} → ${toIso}`;
  }
}

/** Date-only range for compact mobile layouts (no time-of-day). */
function formatInstantRangeShort(fromIso: string, toIso: string, storeTz?: string | null): string {
  try {
    const opts: Intl.DateTimeFormatOptions = { dateStyle: "medium" };
    const from = new Date(fromIso);
    const to = new Date(toIso);
    const tz = storeTz || undefined;
    return `${from.toLocaleString(undefined, { ...opts, timeZone: tz })} → ${to.toLocaleString(undefined, { ...opts, timeZone: tz })}`;
  } catch {
    return `${fromIso} → ${toIso}`;
  }
}

function savedRunStatusClass(status: PayrollRunStatusEnum): string {
  switch (status) {
    case "FINALIZED":
      return "bg-emerald-100 text-emerald-800";
    case "VOIDED":
      return "bg-slate-200 text-slate-600";
    default:
      return "bg-amber-100 text-amber-900";
  }
}

export function PayrollPage() {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const isOwnerAtStore = hasRestaurant?.storeStaffRole === "OWNER";
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const [customFrom, setCustomFrom] = useState<string | null>(null);
  const [customTo, setCustomTo] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<PayrollPreviewRow | null>(null);
  const [mainTab, setMainTab] = useState<"preview" | "saved">("preview");
  const [runs, setRuns] = useState<PayrollRunSummary[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<PayrollRunDetail | null>(null);
  const [runDetailLoading, setRunDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const dateRange = useMemo(() => {
    const range = getReportDateRange(
      datePreset,
      customFrom ? new Date(customFrom) : undefined,
      customTo ? new Date(customTo) : undefined
    );
    return { from: range.from, to: range.to, fromDay: range.from.slice(0, 10), toDay: range.to.slice(0, 10) };
  }, [datePreset, customFrom, customTo]);

  const previewInputForQuery: ReportQueryInput | null = useMemo(
    () =>
      hasRestaurant?.correlationId
        ? { storeCorrelationId: hasRestaurant.correlationId, from: dateRange.from, to: dateRange.to }
        : null,
    [hasRestaurant?.correlationId, dateRange.from, dateRange.to]
  );

  const { data, loading, error, refetch } = useReportQuery<PayrollPreviewResult>(
    PAYROLL_PREVIEW,
    "payrollPreview",
    previewInputForQuery
  );

  const loadRuns = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    setRunsLoading(true);
    setRunsError(null);
    try {
      const { data: res } = await axBe.post<{
        data?: { payrollRuns?: PayrollRunSummary[] };
        errors?: Array<{ message?: string }>;
      }>(GRAPHQL_ENDPOINT, {
        query: PAYROLL_RUNS,
        variables: { storeCorrelationId: hasRestaurant.correlationId, limit: 50 },
      });
      const err = res?.errors?.[0]?.message;
      if (err) {
        setRunsError(err);
        setRuns([]);
        return;
      }
      setRuns(res?.data?.payrollRuns ?? []);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to load payroll runs";
      setRunsError(msg);
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, [axBe, hasRestaurant?.correlationId]);

  useEffect(() => {
    if (hasRestaurant?.correlationId) loadRuns();
  }, [hasRestaurant?.correlationId, loadRuns]);

  const openRunDetail = useCallback(
    async (id: number) => {
      if (!hasRestaurant?.correlationId) return;
      setRunDetailLoading(true);
      setActionMessage(null);
      try {
        const { data: res } = await axBe.post<{
          data?: { payrollRun?: PayrollRunDetail | null };
          errors?: Array<{ message?: string }>;
        }>(GRAPHQL_ENDPOINT, {
          query: PAYROLL_RUN,
          variables: { id, storeCorrelationId: hasRestaurant.correlationId },
        });
        const err = res?.errors?.[0]?.message;
        if (err) {
          setActionMessage(err);
          setSelectedRun(null);
          return;
        }
        setSelectedRun(res?.data?.payrollRun ?? null);
      } catch (e: unknown) {
        setActionMessage(e instanceof Error ? e.message : "Failed to load run");
        setSelectedRun(null);
      } finally {
        setRunDetailLoading(false);
      }
    },
    [axBe, hasRestaurant?.correlationId]
  );

  const handleDateChange = useCallback((preset: ReportDatePreset, from: string, to: string) => {
    setDatePreset(preset);
    if (preset === "custom") {
      setCustomFrom(from);
      setCustomTo(to);
    } else {
      setCustomFrom(null);
      setCustomTo(null);
    }
  }, []);

  const exportCsv = useCallback(() => {
    if (!data?.rows?.length) return;
    const headers = [
      "Employee",
      "Hours",
      "Rate/Salary",
      "Base Pay",
      "Tips Earned",
      "Tips Allocated (net)",
      "Tips Take-Home",
      "Gross Pay",
    ];
    const lines = [
      headers.join(","),
      ...data.rows.map((r) =>
        [
          `"${(r.employeeName ?? "").replace(/"/g, '""')}"`,
          r.hoursWorked.toFixed(2),
          `"${formatRate(r).replace(/"/g, '""')}"`,
          (r.basePayCents / 100).toFixed(2),
          (r.tipsEarnedCents / 100).toFixed(2),
          (r.tipsAllocatedNetCents / 100).toFixed(2),
          (r.tipsTakeHomeCents / 100).toFixed(2),
          (r.grossPayCents / 100).toFixed(2),
        ].join(",")
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payroll-preview-${dateRange.fromDay}-${dateRange.toDay}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data?.rows, dateRange.fromDay, dateRange.toDay]);

  const saveDraft = useCallback(async () => {
    if (!previewInputForQuery?.storeCorrelationId) return;
    setActionLoading(true);
    setActionMessage(null);
    try {
      const variables = {
        input: {
          ...previewInputForQuery,
          timezone: previewInputForQuery.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };
      const { data: res } = await axBe.post<{
        data?: { createPayrollRunDraft?: PayrollRunDetail };
        errors?: Array<{ message?: string }>;
      }>(GRAPHQL_ENDPOINT, { query: CREATE_PAYROLL_RUN_DRAFT, variables });
      const err = res?.errors?.[0]?.message;
      if (err) {
        setActionMessage(err);
        return;
      }
      const created = res?.data?.createPayrollRunDraft;
      setActionMessage(created ? `Saved draft #${created.id}` : "Draft saved");
      await loadRuns();
      if (created?.id) {
        await openRunDetail(created.id);
        setMainTab("saved");
      }
      await refetch();
    } catch (e: unknown) {
      setActionMessage(e instanceof Error ? e.message : "Save draft failed");
    } finally {
      setActionLoading(false);
    }
  }, [axBe, previewInputForQuery, loadRuns, openRunDetail, refetch]);

  const deleteDraft = useCallback(async () => {
    if (!selectedRun || !hasRestaurant?.correlationId || selectedRun.status !== "DRAFT") return;
    setActionLoading(true);
    setActionMessage(null);
    try {
      const { data: res } = await axBe.post<{ errors?: Array<{ message?: string }> }>(GRAPHQL_ENDPOINT, {
        query: DELETE_PAYROLL_RUN,
        variables: { id: selectedRun.id, storeCorrelationId: hasRestaurant.correlationId },
      });
      const err = res?.errors?.[0]?.message;
      if (err) {
        setActionMessage(err);
        return;
      }
      setSelectedRun(null);
      await loadRuns();
      setActionMessage("Draft deleted");
    } catch (e: unknown) {
      setActionMessage(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setActionLoading(false);
    }
  }, [axBe, selectedRun, hasRestaurant?.correlationId, loadRuns]);

  const finalizeRun = useCallback(async () => {
    if (!selectedRun || !hasRestaurant?.correlationId || selectedRun.status !== "DRAFT") return;
    const runId = selectedRun.id;
    setActionLoading(true);
    setActionMessage(null);
    try {
      const { data: res } = await axBe.post<{
        data?: { finalizePayrollRun?: PayrollRunDetail };
        errors?: Array<{ message?: string }>;
      }>(GRAPHQL_ENDPOINT, {
        query: FINALIZE_PAYROLL_RUN,
        variables: { id: runId, storeCorrelationId: hasRestaurant.correlationId },
      });
      const err = res?.errors?.[0]?.message;
      if (err) {
        setActionMessage(err);
        return;
      }
      await loadRuns();
      await openRunDetail(runId);
      setActionMessage("Payroll finalized");
    } catch (e: unknown) {
      setActionMessage(e instanceof Error ? e.message : "Finalize failed");
    } finally {
      setActionLoading(false);
    }
  }, [axBe, selectedRun, hasRestaurant?.correlationId, loadRuns, openRunDetail]);

  const voidRun = useCallback(async () => {
    if (!selectedRun || !hasRestaurant?.correlationId || selectedRun.status !== "FINALIZED") return;
    const runId = selectedRun.id;
    setActionLoading(true);
    setActionMessage(null);
    try {
      const { data: res } = await axBe.post<{
        data?: { voidPayrollRun?: PayrollRunDetail };
        errors?: Array<{ message?: string }>;
      }>(GRAPHQL_ENDPOINT, {
        query: VOID_PAYROLL_RUN,
        variables: { id: runId, storeCorrelationId: hasRestaurant.correlationId },
      });
      const err = res?.errors?.[0]?.message;
      if (err) {
        setActionMessage(err);
        return;
      }
      await loadRuns();
      await openRunDetail(runId);
      setActionMessage("Payroll voided");
    } catch (e: unknown) {
      setActionMessage(e instanceof Error ? e.message : "Void failed");
    } finally {
      setActionLoading(false);
    }
  }, [axBe, selectedRun, hasRestaurant?.correlationId, loadRuns, openRunDetail]);

  const summary = data?.summary;
  const rows = data?.rows ?? [];

  const toolbar = (
    <div className="flex flex-wrap items-center justify-between gap-3 w-full min-w-0">
      <div className="flex items-center gap-1 rounded-lg border border-[#F1F1F2] p-0.5 bg-white">
        <button
          type="button"
          onClick={() => setMainTab("preview")}
          className={`h-[38px] px-3 text-xs font-medium rounded-md transition-all outline-none focus:ring-0 ${
            mainTab === "preview"
              ? "bg-[#FD7657] text-white shadow-sm"
              : "bg-white text-[#6b7280] hover:bg-[#FEF5F4] hover:text-[#FD7657]"
          }`}
        >
          Live preview
        </button>
        <button
          type="button"
          onClick={() => setMainTab("saved")}
          className={`h-[38px] px-3 text-xs font-medium rounded-md transition-all outline-none focus:ring-0 ${
            mainTab === "saved"
              ? "bg-[#FD7657] text-white shadow-sm"
              : "bg-white text-[#6b7280] hover:bg-[#FEF5F4] hover:text-[#FD7657]"
          }`}
        >
          Saved payroll
        </button>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3 min-w-0">
        <span className="inline-block min-w-[150px]">
          <ReportDateRangePicker
            value={datePreset}
            onChange={handleDateChange}
            customFrom={datePreset === "custom" ? customFrom : undefined}
            customTo={datePreset === "custom" ? customTo : undefined}
          />
        </span>
        {isOwnerAtStore && mainTab === "preview" && (
          <>
            <button
              type="button"
              onClick={saveDraft}
              disabled={!rows.length || actionLoading}
              className="h-[38px] px-3 text-xs font-semibold rounded-lg text-white bg-orange border border-orange hover:bg-[#e66a4a] hover:border-[#e66a4a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading ? "Saving…" : "Save as draft"}
            </button>
            <button
              type="button"
              onClick={exportCsv}
              disabled={!rows.length}
              className="h-[38px] px-3 text-xs font-medium rounded-lg border border-[#e5e7eb] bg-white text-[#374151] hover:bg-[#f9fafb] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Export CSV
            </button>
          </>
        )}
        {isOwnerAtStore && mainTab === "saved" && (
          <button
            type="button"
            onClick={() => loadRuns()}
            disabled={runsLoading}
            className="h-[38px] px-3 text-xs font-medium rounded-lg border border-[#e5e7eb] bg-white text-[#374151] hover:bg-[#f9fafb] disabled:opacity-50"
          >
            Refresh list
          </button>
        )}
      </div>
    </div>
  );

  const kpiRow =
    mainTab === "preview" && summary ? (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-[10px] mb-4">
        <div className="rounded-[14px] bg-white p-[10px] border border-[#f1f1f1] shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <p className="text-[14px] text-[#6b7280] mb-1">Total base pay</p>
          <p className="text-lg font-semibold text-[#111827]">
            {loading ? "—" : formatCentsToDollarsWithDollarSign(summary.totalBasePayCents)}
          </p>
        </div>
        <div className="rounded-[14px] bg-white p-[10px] border border-[#f1f1f1] shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <p className="text-[14px] text-[#6b7280] mb-1">Total tips (take-home)</p>
          <p className="text-lg font-semibold text-[#111827]">
            {loading ? "—" : formatCentsToDollarsWithDollarSign(summary.totalTipsTakeHomeCents)}
          </p>
        </div>
        <div className="rounded-[14px] bg-white p-[10px] border border-[#f1f1f1] shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <p className="text-[14px] text-[#6b7280] mb-1">Total gross</p>
          <p className="text-lg font-semibold text-[#111827]">
            {loading ? "—" : formatCentsToDollarsWithDollarSign(summary.totalGrossCents)}
          </p>
        </div>
      </div>
    ) : null;

  const renderSavedEmployeeRow = (er: PayrollEmployeeSnapshot) => (
    <tr key={er.id} className="border-b border-[#e5e7eb]">
      <td className="py-2 pl-2 pr-2 text-sm font-medium text-[#111827]">{er.employeeName}</td>
      <td className="py-2 px-2 text-sm tabular-nums">{er.hoursWorked.toFixed(2)}</td>
      <td className="py-2 px-2 text-sm tabular-nums">{formatCentsToDollarsWithDollarSign(er.basePayCents)}</td>
      <td className="py-2 px-2 text-sm tabular-nums">{formatCentsToDollarsWithDollarSign(er.tipsTakeHomeCents)}</td>
      <td className="py-2 pl-2 pr-4 text-sm tabular-nums font-semibold">{formatCentsToDollarsWithDollarSign(er.grossPayCents)}</td>
    </tr>
  );

  return (
    <ReportPageLayout
      toolbar={toolbar}
      toolbarMarginTop={0}
      kpiRow={kpiRow}
      error={error ?? runsError ?? undefined}
      fillHeight
    >
      {actionMessage && (
        <div className="rounded-lg border border-[#e5e7eb] bg-[#f8fafc] px-3 py-2 mb-3 text-sm text-[#374151]">
          {actionMessage}
        </div>
      )}

      {mainTab === "preview" && (
        <>
          <div className="rounded-xl border border-[#e5e7eb] bg-amber-50/80 border-amber-200 px-3 py-2 mb-4 text-sm text-amber-900">
            Live preview — recalculated from current orders, sessions, and compensation. Same date range is stored when you
            save a draft (UTC instants match reports: half-open interval).
          </div>
          {loading && rows.length === 0 ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <BtnLoader />
            </div>
          ) : (
            <div className="w-full bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden flex flex-col flex-1 min-h-0">
              <div className="flex-1 min-h-0 overflow-auto overflow-x-auto">
                <table className="w-full text-left border-collapse table-auto min-w-[900px]">
                  <thead>
                    <tr>
                      <th className={`pl-2 pr-2 ${headerCellClass}`}>Employee</th>
                      <th className={`px-2 ${headerCellClass}`}>Hours</th>
                      <th className={`px-2 ${headerCellClass}`}>Rate / Salary</th>
                      <th className={`px-2 ${headerCellClass}`}>Base pay</th>
                      <th className={`px-2 ${headerCellClass}`}>Tips earned</th>
                      <th className={`px-2 ${headerCellClass}`}>Tips allocated (net)</th>
                      <th className={`px-2 ${headerCellClass}`}>Tips take-home</th>
                      <th className={`pl-2 pr-4 ${headerCellClass}`}>Gross pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-8 text-center text-[#6b7280] text-sm">
                          No payroll data for this period.
                        </td>
                      </tr>
                    ) : (
                      rows.map((row) => (
                        <tr
                          key={row.employeeId}
                          onClick={() => setSelectedRow(row)}
                          className={`border-b border-[#e5e7eb] hover:bg-[#f8fafc] transition-colors duration-100 cursor-pointer ${
                            (row.warnings?.length ?? 0) > 0 ? "bg-amber-50/50" : ""
                          }`}
                        >
                          <td className="py-3 pl-2 pr-2 text-sm font-medium text-[#111827]">{row.employeeName ?? "—"}</td>
                          <td className="py-3 px-2 text-sm tabular-nums text-[#374151]">{row.hoursWorked.toFixed(2)}</td>
                          <td className="py-3 px-2 text-sm text-[#374151]">{formatRate(row)}</td>
                          <td className="py-3 px-2 text-sm tabular-nums text-[#111827]">
                            {formatCentsToDollarsWithDollarSign(row.basePayCents)}
                          </td>
                          <td className="py-3 px-2 text-sm tabular-nums text-[#374151]">
                            {formatCentsToDollarsWithDollarSign(row.tipsEarnedCents)}
                          </td>
                          <td className="py-3 px-2 text-sm tabular-nums text-[#374151]">
                            {formatCentsToDollarsWithDollarSign(row.tipsAllocatedNetCents)}
                          </td>
                          <td className="py-3 px-2 text-sm tabular-nums text-[#374151]">
                            {formatCentsToDollarsWithDollarSign(row.tipsTakeHomeCents)}
                          </td>
                          <td className="py-3 pl-2 pr-4 text-sm tabular-nums font-semibold text-[#111827]">
                            {formatCentsToDollarsWithDollarSign(row.grossPayCents)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {mainTab === "saved" && (
        <div className="space-y-4">
          <div className="rounded-xl border border-[#e5e7eb] bg-slate-50 px-3 py-2 text-sm text-[#334155]">
            Saved runs are frozen snapshots. Finalized runs cannot be edited — void and create a new run if you need a
            correction. Calculation version is shown for audit.
          </div>
          {runsLoading && runs.length === 0 ? (
            <div className="flex justify-center py-12">
              <BtnLoader />
            </div>
          ) : runs.length === 0 ? (
            <div className="w-full rounded-xl border border-[#F1F1F2] bg-white py-8 text-center text-sm text-[#6b7280]">
              No saved payroll runs yet.
            </div>
          ) : (
            <>
              <ul className="md:hidden flex flex-col gap-3">
                {runs.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => openRunDetail(r.id)}
                      className="w-full rounded-xl border border-[#F1F1F2] bg-white p-4 text-left shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-colors hover:bg-[#f8fafc] active:bg-[#f1f5f9]"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-base font-semibold text-[#111827] tabular-nums">Run #{r.id}</span>
                        <span
                          className={`shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${savedRunStatusClass(r.status)}`}
                        >
                          {r.status}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-snug text-[#374151]">
                        {formatInstantRangeShort(r.periodFrom, r.periodTo, r.storeTimezone)}
                      </p>
                      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-[#e5e7eb] pt-3 text-sm">
                        <dt className="text-[#6b7280]">Employees</dt>
                        <dd className="text-right tabular-nums text-[#111827]">{r.employeeCount}</dd>
                        <dt className="text-[#6b7280]">Gross</dt>
                        <dd className="text-right tabular-nums font-medium text-[#111827]">
                          {formatCentsToDollarsWithDollarSign(r.totalGrossPayCents)}
                        </dd>
                        <dt className="col-span-2 text-xs font-medium uppercase tracking-wide text-[#6b7280]">Version</dt>
                        <dd className="col-span-2 break-all font-mono text-xs text-[#4b5563]">{r.calculationVersion}</dd>
                      </dl>
                    </button>
                  </li>
                ))}
              </ul>

              <div className="hidden md:block w-full overflow-x-auto rounded-xl border border-[#F1F1F2] bg-white">
                <table className="w-full min-w-[640px] text-left border-collapse">
                  <thead>
                    <tr>
                      <th className={headerCellClass}>ID</th>
                      <th className={`${headerCellClass} min-w-[200px]`}>Period</th>
                      <th className={headerCellClass}>Status</th>
                      <th className={headerCellClass}>Employees</th>
                      <th className={headerCellClass}>Gross</th>
                      <th className={headerCellClass}>Version</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => (
                      <tr
                        key={r.id}
                        onClick={() => openRunDetail(r.id)}
                        className="cursor-pointer border-b border-[#e5e7eb] hover:bg-[#f8fafc]"
                      >
                        <td className="py-2.5 pl-3 pr-2 text-sm tabular-nums">{r.id}</td>
                        <td className="py-2.5 px-2 text-sm text-[#374151]">
                          {formatInstantRange(r.periodFrom, r.periodTo, r.storeTimezone)}
                        </td>
                        <td className="py-2.5 px-2 text-sm">{r.status}</td>
                        <td className="py-2.5 px-2 text-sm tabular-nums">{r.employeeCount}</td>
                        <td className="py-2.5 px-2 text-sm tabular-nums">
                          {formatCentsToDollarsWithDollarSign(r.totalGrossPayCents)}
                        </td>
                        <td className="py-2.5 pl-2 pr-3 text-xs font-mono text-[#6b7280]">{r.calculationVersion}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {selectedRow && (
        <div
          className="fixed inset-x-0 bottom-0 top-14 z-50 flex justify-end bg-black/60"
          onClick={() => setSelectedRow(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-md bg-white shadow-xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Payroll row details"
          >
            <div className="sticky top-0 bg-white border-b border-[#e5e7eb] px-4 py-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#111827]">{selectedRow.employeeName}</h2>
              <button
                type="button"
                onClick={() => setSelectedRow(null)}
                className="p-1 rounded hover:bg-gray-100 text-[#6b7280]"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-[#6b7280]">Hours worked</span>
                <span className="tabular-nums">{selectedRow.hoursWorked.toFixed(2)}</span>
                <span className="text-[#6b7280]">Base pay</span>
                <span className="tabular-nums">{formatCentsToDollarsWithDollarSign(selectedRow.basePayCents)}</span>
                <span className="text-[#6b7280]">Tips take-home</span>
                <span className="tabular-nums">{formatCentsToDollarsWithDollarSign(selectedRow.tipsTakeHomeCents)}</span>
                <span className="text-[#6b7280]">Gross pay</span>
                <span className="tabular-nums font-semibold">{formatCentsToDollarsWithDollarSign(selectedRow.grossPayCents)}</span>
              </div>
              {selectedRow.hoursBreakdown?.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-[#374151] mb-2">Hours by role</h3>
                  <ul className="space-y-1 text-sm">
                    {selectedRow.hoursBreakdown.map((b, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span>{b.jobRoleName}</span>
                        <span className="tabular-nums">
                          {b.hours.toFixed(2)} h × {formatCentsToDollarsWithDollarSign(b.rateCents)}/hr ={" "}
                          {formatCentsToDollarsWithDollarSign(b.basePayCents)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedRow.warnings?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <h3 className="text-sm font-medium text-amber-900 mb-1">Warnings</h3>
                  <ul className="list-disc list-inside text-sm text-amber-800 space-y-0.5">
                    {selectedRow.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {(selectedRun || runDetailLoading) && (
        <div
          className="fixed inset-x-0 bottom-0 top-14 z-50 flex justify-end bg-black/60"
          onClick={() => !actionLoading && setSelectedRun(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-3xl bg-white shadow-xl overflow-y-auto flex flex-col"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Payroll run detail"
          >
            <div className="sticky top-0 bg-white border-b border-[#e5e7eb] px-4 py-3 flex items-center justify-between z-10">
              <div>
                <h2 className="text-lg font-semibold text-[#111827]">Payroll run #{selectedRun?.id ?? "…"}</h2>
                {selectedRun && (
                  <p className="text-xs text-[#6b7280] mt-1">
                    {formatInstantRange(selectedRun.periodFrom, selectedRun.periodTo, selectedRun.storeTimezone)} ·{" "}
                    {selectedRun.calculationVersion}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setSelectedRun(null)}
                disabled={actionLoading}
                className="p-1 rounded hover:bg-gray-100 text-[#6b7280] disabled:opacity-50"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            {runDetailLoading ? (
              <div className="flex justify-center py-16">
                <BtnLoader />
              </div>
            ) : selectedRun ? (
              <div className="p-4 flex-1 space-y-4">
                <div className="flex flex-wrap gap-2 items-center">
                  <span
                    className={`text-xs font-semibold px-2 py-1 rounded ${
                      selectedRun.status === "DRAFT"
                        ? "bg-blue-100 text-blue-900"
                        : selectedRun.status === "FINALIZED"
                          ? "bg-green-100 text-green-900"
                          : "bg-gray-200 text-gray-800"
                    }`}
                  >
                    {selectedRun.status}
                  </span>
                  {selectedRun.createdByEmail && (
                    <span className="text-xs text-[#6b7280]">Created by {selectedRun.createdByEmail}</span>
                  )}
                </div>
                {isOwnerAtStore && (
                  <div className="flex flex-wrap gap-2">
                    {selectedRun.status === "DRAFT" && (
                      <>
                        <button
                          type="button"
                          onClick={() => finalizeRun()}
                          disabled={actionLoading}
                          className="h-9 px-3 text-xs font-semibold rounded-lg text-white bg-orange border border-orange shadow-sm hover:bg-[#e66a4a] hover:border-[#e66a4a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Finalize
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm("Delete this draft?")) void deleteDraft();
                          }}
                          disabled={actionLoading}
                          className="h-9 px-3 text-xs font-medium rounded-lg border border-red-300 text-red-800 hover:bg-red-50 disabled:opacity-50"
                        >
                          Delete draft
                        </button>
                      </>
                    )}
                    {selectedRun.status === "FINALIZED" && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm("Void this finalized payroll? You can create a new run after.")) void voidRun();
                        }}
                        disabled={actionLoading}
                        className="h-9 px-3 text-xs font-medium rounded-lg border border-amber-500 text-amber-900 hover:bg-amber-50 disabled:opacity-50"
                      >
                        Void
                      </button>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-[#6b7280]">Total minutes</p>
                    <p className="font-semibold tabular-nums">{selectedRun.totalMinutesWorked}</p>
                  </div>
                  <div>
                    <p className="text-[#6b7280]">Base pay</p>
                    <p className="font-semibold tabular-nums">{formatCentsToDollarsWithDollarSign(selectedRun.totalBasePayCents)}</p>
                  </div>
                  <div>
                    <p className="text-[#6b7280]">Tips take-home</p>
                    <p className="font-semibold tabular-nums">
                      {formatCentsToDollarsWithDollarSign(selectedRun.totalTipsTakeHomeCents)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#6b7280]">Gross</p>
                    <p className="font-semibold tabular-nums">{formatCentsToDollarsWithDollarSign(selectedRun.totalGrossPayCents)}</p>
                  </div>
                </div>
                <div className="overflow-x-auto border border-[#e5e7eb] rounded-lg">
                  <table className="w-full text-left text-sm min-w-[640px]">
                    <thead>
                      <tr className="bg-[#f8fafc]">
                        <th className="py-2 pl-2">Employee</th>
                        <th className="py-2 px-2">Hours</th>
                        <th className="py-2 px-2">Base</th>
                        <th className="py-2 px-2">Tips TH</th>
                        <th className="py-2 pr-2">Gross</th>
                      </tr>
                    </thead>
                    <tbody>{selectedRun.employees?.map(renderSavedEmployeeRow)}</tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </ReportPageLayout>
  );
}
